import { Button } from 'app/components/ui/button'
import { ArrowLeftIcon, ArrowUpIcon, ChatCircleTextIcon, TextAaIcon, WarningCircleIcon } from '@phosphor-icons/react'
import moment from 'moment'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { MessageCard } from 'app/components/contacts/inbox/MessageCard'
import { SendLaterButton } from 'app/components/sms-scheduling/SendLaterButton'
import { SmsTemplatePicker } from 'app/components/sms-templates/TemplatePicker'
import { useSmsScheduling } from 'app/hooks/useSmsScheduling'
import { substituteTokens } from 'app/lib/messageTemplates'
import Spinner from 'app/components/Spinner'
import { ThreadMessage } from './useContactMessages'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { cn } from 'app/lib/utils'
import { bg } from 'lib/background'
import { notifyError } from 'app/services/notify.client'
import { ContactJson } from 'app/types/entities'

/** Ceiling for the auto-growing composer, about six lines. */
const TEXTAREA_MAX_PX = 132

/** Day separator, so a thread spanning weeks stays readable. */
function DayDivider({ date }: { date: string }) {
  const day = moment(date)
  const label = day.isSame(moment(), `day`)
    ? `Today`
    : day.isSame(moment().subtract(1, `day`), `day`)
      ? `Yesterday`
      : day.format(`MMM D, YYYY`)
  return (
    <div className="flex items-center justify-center py-1">
      <span className="rounded bg-surface-muted px-2 py-0.5 text-label-xs text-subtle">{label}</span>
    </div>
  )
}

/**
 * The focus card's reverse face: the contact's SMS thread with a composer.
 *
 * Bubbles are the app's own `MessageCard`, the same component the contact
 * drawer's Inbox tab renders, so a thread looks identical wherever it is read
 * and the outbound/AI/inbound rules stay in one place.
 */
export function ConversationPanel({
  contact,
  messages,
  loading,
  sending,
  failed,
  active,
  framed = true,
  onSend,
  onBack,
  onRetry,
}: {
  contact: ContactJson
  messages: ThreadMessage[]
  loading: boolean
  sending: boolean
  /** A read failed; distinct from a genuinely empty thread. */
  failed: boolean
  /** True while this face is showing; drives autofocus and scroll-to-latest. */
  active: boolean
  /**
   * Draw the panel's own card. False when something else already provides one:
   * inside a dialog this would be a second border and a second shadow drawn
   * around content that is already in a panel.
   */
  framed?: boolean
  onSend: (text: string) => Promise<boolean>
  onBack: () => void
  onRetry: () => void
}) {
  const [draft, setDraft] = useState(``)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  /**
   * Grow the box to fit what has been typed, up to a ceiling.
   *
   * A fixed single-row textarea turns Shift+Enter into a trap: the newline goes
   * in but the box does not grow, so the message scrolls inside a 36px slot and
   * you lose sight of what you wrote. Reset to `auto` first or scrollHeight
   * only ever ratchets upward and the box never shrinks back.
   */
  useLayoutEffect(() => {
    const node = inputRef.current
    if (!node) return
    node.style.height = `auto`
    const wanted = node.scrollHeight
    node.style.height = `${Math.min(wanted, TEXTAREA_MAX_PX)}px`
    // Only show a scrollbar once the box has actually stopped growing.
    // Left on `auto` it renders a track against sub-pixel rounding even on an
    // empty line, which reads as a defect on a one-line composer.
    node.style.overflowY = wanted > TEXTAREA_MAX_PX ? `auto` : `hidden`
  }, [draft, active])
  const firstName = contact.firstName || contact.name?.split(` `)[0] || `contact`

  // Pin to the latest message whenever the thread changes or the face turns
  // over. Layout effect so the jump happens before paint, never as a visible
  // scroll after the flip settles.
  useLayoutEffect(() => {
    if (!active) return
    const node = scrollRef.current
    if (node) node.scrollTop = node.scrollHeight
  }, [active, messages.length, loading])

  useEffect(() => {
    if (active) inputRef.current?.focus()
  }, [active])

  const submit = async () => {
    const body = draft.trim()
    if (!body || sending) return
    setDraft(``)
    const ok = await onSend(body)
    // Hand the text back rather than losing what they typed.
    if (!ok) setDraft(body)
  }

  // The practice clock and the compliance opt-out gate, assembled the same way
  // every other single-recipient composer assembles them.
  const { scheduled, timezone } = useSmsScheduling({
    contactId: contact.id,
    phone: contact.cellPhone,
    locationId: contact.locationId,
  })

  const canText = !!contact.cellPhone

  return (
    <div
      className={cn(
        `flex h-full flex-col overflow-hidden bg-surface`,
        framed && `rounded-2xl border border-default shadow-elevation-4`,
      )}
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-subtle px-4 py-3">
        <Button variant="subtle" size="icon-sm" onClick={onBack} aria-label="Back to contact">
          <ArrowLeftIcon size={16} weight="bold" />
        </Button>
        <div className="min-w-0">
          <p className="truncate text-ui-sm text-strong">{contact.name || `Unknown contact`}</p>
          {contact.cellPhone && (
            <p className="truncate text-body-xs tabular-nums text-subtle">{formatPhoneNumber(contact.cellPhone)}</p>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-1 overflow-y-auto px-4 py-3">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner size={5} />
          </div>
        ) : failed && messages.length === 0 ? (
          // A read that failed is not an empty conversation, and must never be
          // drawn as one: it would read as "no history" for someone who has it.
          <div className="flex h-full flex-col items-center justify-center text-center text-body-sm">
            <WarningCircleIcon size={28} className="text-red-400" />
            <p className="mt-2 text-body-sm text-default">Could not load this conversation.</p>
            <Button variant="link" size="inline" onClick={onRetry} className="mt-2">
              Try again
            </Button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <ChatCircleTextIcon size={28} className="text-minimal" />
            <p className="mt-2 text-body-sm text-subtle">No texts with {firstName} yet.</p>
            <p className="text-body-xs text-minimal">Start the conversation below.</p>
          </div>
        ) : (
          messages.map((message, index) => {
            const previous = messages[index - 1]
            const newDay = !previous || !moment(message.createdDate).isSame(previous.createdDate, `day`)
            return (
              <div key={message.id ?? index}>
                {newDay && message.createdDate && <DayDivider date={message.createdDate} />}
                <div className={cn(message.pending && `opacity-60`)}>
                  <MessageCard contact={contact} message={message} />
                </div>
                {message.failed && (
                  <p className="flex items-center justify-end gap-1 pr-1 text-label-xs text-red-600">
                    <WarningCircleIcon size={12} weight="fill" /> Not delivered
                  </p>
                )}
              </div>
            )
          })
        )}
      </div>

      <div className="shrink-0 border-t border-subtle p-3">
        {canText ? (
          <div className="flex items-end gap-2">
            {/* Templates resolve against THIS contact before they land in the
                box, so what you see is what sends. `keepUnresolved` leaves any
                token we cannot fill visible as text: a human is about to read
                this line, and a silently dropped token reads as a typo you
                wrote rather than data we lacked. */}
            <SmsTemplatePicker
              currentBody={draft}
              renderTemplate={(template) => substituteTokens(template.body, { contact }).text}
              onSelect={(template) =>
                setDraft(substituteTokens(template.body, { contact }, { keepUnresolved: true }).text)
              }
              trigger={
                <button
                  type="button"
                  aria-label="Insert a template"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-default text-subtle transition hover:border-strong hover:bg-surface-subtle hover:text-strong"
                >
                  <TextAaIcon size={16} weight="bold" />
                </button>
              }
            />
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter sends, Shift+Enter breaks the line, like every chat.
                if (event.key === `Enter` && !event.shiftKey) {
                  event.preventDefault()
                  bg(submit())
                }
              }}
              placeholder={`Text ${firstName}`}
              className="min-h-9 flex-1 resize-none overflow-hidden rounded-xl border border-default px-3 py-2 text-body-sm leading-relaxed text-strong placeholder:text-minimal focus:border-primary-400 focus:outline-none"
            />
            <Button
              size="icon"
              shape="pill"
              onClick={() => bg(submit())}
              disabled={!draft.trim() || sending}
              aria-label="Send text"
              className="shrink-0"
            >
              {sending ? <Spinner size={4} /> : <ArrowUpIcon size={16} weight="bold" />}
            </Button>
            <SendLaterButton
              timezone={timezone}
              // `scheduling` as much as an empty draft: without it a second
              // click while the first schedule is still in flight queues a
              // second held message, and a thread is allowed exactly one.
              disabled={!draft.trim() || scheduled.scheduling}
              unavailableReason={scheduled.unavailableReason}
              onSchedule={async (sendAtLocal) => {
                const result = await scheduled.schedule(draft.trim(), sendAtLocal)
                if (result.ok) setDraft(``)
                else notifyError(result.reason ?? `Couldn't schedule this message`)
              }}
            />
          </div>
        ) : (
          <p className="py-1 text-center text-body-sm text-subtle">No mobile number on file for {firstName}.</p>
        )}
      </div>
    </div>
  )
}
